<template>
  <div class="services-management">
    <!-- Header -->
    <div class="mb-4">
      <div class="d-flex align-center ga-3 mb-2">
        <v-icon color="primary" size="24">$folder</v-icon>
        <h6 class="text-subtitle-1">{{ t('employees.manageServices') }}</h6>
      </div>
      <p class="text-body-2 text-grey">
        {{ employeeName ? 
          t('employees.selectServicesForEmployee', { name: employeeName }) : 
          t('employees.selectServicesForNewEmployee') 
        }}
      </p>
    </div>

    <!-- Search services -->
    <v-text-field
      v-model="searchQuery"
      :placeholder="t('services.searchServices')"
      prepend-inner-icon="$search"
      variant="outlined"
      density="compact"
      clearable
      class="mb-4"
    />

    <!-- Loading state -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
      <p class="text-body-2 text-grey mt-2">{{ t('common.loading') }}</p>
    </div>

    <!-- Services list -->
    <div v-else>
      <v-alert v-if="filteredServices.length === 0" type="info" variant="tonal" density="compact">
        {{ searchQuery ? t('services.noServicesFound') : t('services.noServicesAvailable') }}
      </v-alert>

      <div v-else class="services-list" style="max-height: 400px; overflow-y: auto;">
        <div
          v-for="service in filteredServices"
          :key="service.uuid"
          class="service-item d-flex align-start ga-3 mb-3 pa-2"
          :class="{ 'service-item--disabled': updating }"
        >
          <CtCheckbox2
            :model-value="selectedServiceIds.includes(service.uuid!)"
            :disabled="updating"
            color="primary"
            size="medium"
            wrapper-class="mt-1"
            @update:model-value="(checked: boolean) => toggleService(service.uuid!, checked)"
          />
          
          <div class="flex-grow-1" @click="!updating && toggleService(service.uuid!, !selectedServiceIds.includes(service.uuid!))">
            <div class="d-flex flex-column" style="cursor: pointer;">
              <span class="text-body-1 font-weight-medium">{{ service.name }}</span>
              <span v-if="service.description" class="text-body-2 text-grey mt-1">
                {{ service.description }}
              </span>
              <div class="d-flex align-center ga-4 mt-2">
                <span class="text-caption text-grey d-flex align-center">
                  <v-icon size="12" class="mr-1">$clock</v-icon>
                  {{ service.duration }}min
                </span>
                <span class="text-caption text-grey d-flex align-center">
                  <v-icon size="12" class="mr-1">$money</v-icon>
                  {{ formatPrice(service.price) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action buttons (only show if showActions is true) -->
    <div v-if="showActions" class="d-flex justify-end ga-3 mt-6">
      <v-btn
        variant="text"
        :disabled="updating"
        @click="resetChanges"
      >
        {{ t('common.reset') }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!hasChanges || updating"
        :loading="updating"
        @click="saveChanges"
      >
        {{ t('common.save') }}
      </v-btn>
    </div>

    <!-- Info about changes (show if no actions but has changes) -->
    <v-alert v-else-if="hasChanges" type="info" variant="tonal" density="compact" class="mt-4">
      <div class="text-body-2">
        {{ t('employees.servicesWillBeUpdated', { count: selectedServiceIds.length }) }}
      </div>
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import type { PropType } from 'vue'
import type { Employee } from '~/interfaces/employeeInterface'
import type { ServiceInterface } from '~/interfaces/serviceInterface'
import { useServicesComposable } from '~/composables/api/services'
import { useEmployeesComposable } from '~/composables/api/employee'
import { getEmployeeDisplayName } from '~/utils/employeeUtils'
import CtCheckbox2 from '~/components/common/CtCheckbox2.vue'

const props = defineProps({
  employee: {
    type: Object as PropType<Employee | null>,
    default: null
  },
  companyUuid: {
    type: String,
    required: true
  },
  initialSelectedServices: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  showActions: {
    type: Boolean,
    default: true
  },
  autoLoad: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits<{
  'services-changed': [serviceIds: string[]]
  'services-updated': [services: ServiceInterface[]]
  'employee-updated': [employee: Employee]
}>()

const { t } = useI18n()
const { getServicesList } = useServicesComposable()
const { updateEmployee } = useEmployeesComposable()

// Reactive state
const loading = ref(false)
const updating = ref(false)
const searchQuery = ref('')
const services = ref<ServiceInterface[]>([])
const selectedServiceIds = ref<string[]>([])
const initialServiceIds = ref<string[]>([])

// Computed properties
const employeeName = computed(() => 
  props.employee ? getEmployeeDisplayName(props.employee, t) : null
)

const filteredServices = computed(() => {
  if (!searchQuery.value) return services.value
  
  const query = searchQuery.value.toLowerCase()
  return services.value.filter(service => 
    service.name.toLowerCase().includes(query) ||
    service.description?.toLowerCase().includes(query)
  )
})

const hasChanges = computed(() => {
  const current = [...selectedServiceIds.value].sort()
  const initial = [...initialServiceIds.value].sort()
  return JSON.stringify(current) !== JSON.stringify(initial)
})

const toggleService = (serviceUuid: string, checked: boolean) => {
  if (updating.value) return
  
  if (checked) {
    if (!selectedServiceIds.value.includes(serviceUuid)) {
      selectedServiceIds.value.push(serviceUuid)
    }
  } else {
    selectedServiceIds.value = selectedServiceIds.value.filter(id => id !== serviceUuid)
  }
  
  // Emit changes
  emit('services-changed', selectedServiceIds.value)
}

const loadServices = async () => {
  loading.value = true
  try {
    const response = await getServicesList(
      props.companyUuid,
      1,
      100,
      'name',
      'ASC'
    ) as { data: ServiceInterface[] }

    services.value = response.data || []
    
    // Set initial selections
    const currentServiceIds = props.employee?.services?.map(s => s.uuid) || props.initialSelectedServices || []
    selectedServiceIds.value = [...currentServiceIds]
    initialServiceIds.value = [...currentServiceIds]
    
    // Emit initial state
    emit('services-changed', selectedServiceIds.value)
    
  } catch (error) {
    console.error('Error loading services:', error)
    useSnackbarStore().setSnackbar(
      t('services.errors.loadFailed'),
      'error'
    )
  } finally {
    loading.value = false
  }
}

const saveChanges = async () => {
  if (!props.employee || !hasChanges.value) return
  
  updating.value = true
  try {
    if (!props.employee.address) {
      throw new Error('Employee address is undefined')
    }

    // Update employee using the proper composable signature
    await updateEmployee(
      props.companyUuid,
      props.employee.uuid!,
      props.employee.address,
      props.employee.status || 'active',
      selectedServiceIds.value,
      false // all_services = false since we're selecting specific services
    )

    // Create updated employee object
    const updatedServices = services.value.filter(service => 
      selectedServiceIds.value.includes(service.uuid!)
    )
    
    const updatedEmployee: Employee = {
      ...props.employee,
      services: updatedServices
    }

    emit('employee-updated', updatedEmployee)
    emit('services-updated', updatedServices)
    
    // Update initial state
    initialServiceIds.value = [...selectedServiceIds.value]
    
    useSnackbarStore().setSnackbar(
      t('employees.servicesUpdatedSuccessfully'),
      'success'
    )
    
  } catch (error) {
    console.error('Error updating employee services:', error)
    useSnackbarStore().setSnackbar(
      t('employees.errors.updateServicesFailed'),
      'error'
    )
  } finally {
    updating.value = false
  }
}

const resetChanges = () => {
  selectedServiceIds.value = [...initialServiceIds.value]
  emit('services-changed', selectedServiceIds.value)
}

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  }).format(price / 100) // Assuming price is in cents
}

// Expose methods for parent components
defineExpose({
  loadServices,
  saveChanges,
  resetChanges,
  getSelectedServices: () => services.value.filter(s => selectedServiceIds.value.includes(s.uuid!)),
  getSelectedServiceIds: () => selectedServiceIds.value,
  hasChanges
})

// Watch for prop changes
watch(() => props.initialSelectedServices, (newServices) => {
  if (newServices) {
    selectedServiceIds.value = [...newServices]
    initialServiceIds.value = [...newServices]
  }
})

// Auto-load on mount if enabled
onMounted(() => {
  if (props.autoLoad) {
    loadServices()
  }
})
</script>

<style scoped>
.service-item {
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.service-item:hover:not(.service-item--disabled) {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.service-item--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.services-list {
  border: 1px solid rgba(var(--v-theme-outline), 0.2);
  border-radius: 8px;
  padding: 12px;
}
</style>